import type { ReactNode } from 'react';

/**
 * Os ícones do LabManager no celular (onda 308, spec 06 §4 (f)).
 *
 * ── Por que desenho próprio ─────────────────────────────────────────
 *
 * O menu do desktop usa caracteres (▣, ≡, ♨…). No celular o aparelho troca
 * alguns deles por emoji colorido — e o mesmo símbolo sai diferente num
 * Samsung, num iPhone e num Windows. A barra de baixo e a folha «Mais» não
 * podem depender disso: cada ícone aqui é um SVG de 24 × 24, traço de 1,75
 * na cor do texto (`currentColor`), pontas e junções arredondadas, sem
 * preenchimento. Segue o tema sozinho, claro ou escuro, e o nome da função
 * vai escrito embaixo — o ícone nunca é a única pista.
 *
 * Trinta e um deles saem do canvas «LabManager no celular» (os `<symbol>` do
 * topo da página); `alerta`, `ajustes`, `exportar`, `cadastros` e `campo`
 * o canvas não tinha e foram desenhados na mesma grade.
 *
 * O menu do desktop continua com os sinais de hoje: trocar lá é outra
 * conversa.
 */

export type NomeDoIcone =
  | 'os' | 'fila' | 'estufa' | 'receber' | 'mais' | 'amostras' | 'qr' | 'busca'
  | 'painel' | 'cronograma' | 'verificar' | 'relatorio' | 'equipamento' | 'descarte'
  | 'mapa' | 'medicao' | 'campo' | 'pessoa' | 'tema' | 'sair' | 'instalar' | 'relatar'
  | 'filtro' | 'fechar' | 'direita' | 'esquerda' | 'volta' | 'pontos' | 'semrede'
  | 'normas' | 'frasco' | 'ok' | 'alerta' | 'ajustes' | 'exportar' | 'cadastros'
  | 'cone' | 'sino' | 'anexo' | 'casa';

/** Um desenho por nome; o traço, a cor e o tamanho vêm do `<svg>` e do CSS. */
const DESENHO: Record<NomeDoIcone, ReactNode> = {
  os: (
    <>
      <rect x="5" y="4" width="14" height="17" rx="1" />
      <path d="M9 4V2.8h6V4" />
      <path d="M8.5 10h7M8.5 13.5h7M8.5 17h4" />
    </>
  ),
  fila: (
    <>
      <path d="M9.5 6h10.5M9.5 12h10.5M9.5 18h10.5" />
      <path d="M4.5 6h1.5M4.5 12h1.5M4.5 18h1.5" />
    </>
  ),
  estufa: (
    <>
      <rect x="3.5" y="3.5" width="17" height="17" rx="1" />
      <path d="M3.5 8h17" />
      <rect x="7" y="11" width="10" height="6.5" />
      <path d="M6.5 5.8h.01M9.5 5.8h.01M13.5 5.8h4" />
      <path d="M10 16c.9-.9-.9-1.6 0-2.6M14 16c.9-.9-.9-1.6 0-2.6" />
    </>
  ),
  receber: (
    <>
      <path d="M12 3v8.5M8.8 8.5 12 11.7l3.2-3.2" />
      <path d="M8 7H5.6L3.5 13.5V20h17v-6.5L18.4 7H16" />
      <path d="M3.5 13.5h4.6a3.9 3.9 0 0 0 7.8 0h4.6" />
    </>
  ),
  mais: (
    <>
      <rect x="4" y="4" width="6.5" height="6.5" />
      <rect x="13.5" y="4" width="6.5" height="6.5" />
      <rect x="4" y="13.5" width="6.5" height="6.5" />
      <rect x="13.5" y="13.5" width="6.5" height="6.5" />
    </>
  ),
  amostras: (
    <>
      <path d="M8 3h8M9.2 3v4L5.5 11.5V21h13v-9.5L14.8 7V3" />
      <path d="M5.5 14.5h13" />
    </>
  ),
  qr: (
    <>
      <rect x="3.5" y="3.5" width="7" height="7" />
      <rect x="13.5" y="3.5" width="7" height="7" />
      <rect x="3.5" y="13.5" width="7" height="7" />
      <path d="M6.5 7h1M16.5 7h1M6.5 17h1" />
      <path d="M13.5 13.5h3v3M20.5 13.5v.01M13.5 20.5h.01M17 20.5h3.5V17" />
    </>
  ),
  busca: (
    <>
      <circle cx="10.5" cy="10.5" r="6.5" />
      <path d="M15.5 15.5 20.5 20.5" />
    </>
  ),
  painel: (
    <>
      <path d="M4 20h16" />
      <path d="M7 16.5v-5M12 16.5V6M17 16.5v-8" />
    </>
  ),
  cronograma: (
    <>
      <path d="M4 4v16h16" />
      <path d="M8 8h6M11 12h7M8 16h5" />
    </>
  ),
  verificar: (
    <>
      <path d="M12 3l7 3v5.5c0 4.3-3 7.7-7 9.5-4-1.8-7-5.2-7-9.5V6z" />
      <path d="M8.8 12.2 11 14.4l4.3-4.6" />
    </>
  ),
  relatorio: (
    <>
      <path d="M6 3h8.5L19 7.5V21H6z" />
      <path d="M14 3v5h5" />
      <path d="M9 12.5h7M9 16h7" />
    </>
  ),
  equipamento: (
    <>
      <path d="M4.5 17.5a7.5 7.5 0 1 1 15 0" />
      <path d="M12 14l3.4-4" />
      <path d="M3.5 20.5h17" />
    </>
  ),
  descarte: (
    <path d="M4.5 7h15M9.5 7V4.5h5V7M6.5 7l1 13.5h9l1-13.5" />
  ),
  mapa: (
    <>
      <path d="M12 21s-6.5-6.2-6.5-11a6.5 6.5 0 0 1 13 0c0 4.8-6.5 11-6.5 11z" />
      <circle cx="12" cy="10" r="2.3" />
    </>
  ),
  medicao: (
    <>
      <path d="M3.5 15.5l12-12 5 5-12 12z" />
      <path d="M7 12l2 2M10 9l2 2M13 6l2 2" />
    </>
  ),
  /* O tripé do campo: três pernas saindo do mesmo ponto e a travessa. */
  campo: (
    <>
      <path d="M12 3 6 21M12 3l6 18M12 3v13" />
      <path d="M8.5 13h7" />
    </>
  ),
  pessoa: (
    <>
      <circle cx="12" cy="8.5" r="3.5" />
      <path d="M5 20c.8-3.6 3.6-5.5 7-5.5s6.2 1.9 7 5.5" />
    </>
  ),
  /* O único com meia-lua cheia: claro e escuro numa figura só. */
  tema: (
    <>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 4a8 8 0 0 1 0 16z" fill="currentColor" />
    </>
  ),
  sair: (
    <>
      <path d="M10 4H5v16h5" />
      <path d="M14 8l4 4-4 4M18 12H9.5" />
    </>
  ),
  instalar: (
    <>
      <rect x="7" y="2.5" width="10" height="19" rx="1.5" />
      <path d="M12 7v6.5M9.2 10.8 12 13.6l2.8-2.8" />
      <path d="M11 18.3h2" />
    </>
  ),
  relatar: (
    <>
      <path d="M4 5h16v11H9.5L4 20z" />
      <path d="M8 9.5h8M8 12.5h5" />
    </>
  ),
  filtro: (
    <path d="M4 5h16l-6 7.5V19l-4 1.5v-8z" />
  ),
  fechar: (
    <path d="M6 6l12 12M18 6 6 18" />
  ),
  direita: (
    <path d="M9 5l7 7-7 7" />
  ),
  esquerda: (
    <path d="M15 5l-7 7 7 7" />
  ),
  volta: (
    <path d="M19 12H5M11 6l-6 6 6 6" />
  ),
  /* Três pontos: um traço de comprimento zero, engrossado, com ponta redonda. */
  pontos: (
    <path d="M5.5 12h.01M12 12h.01M18.5 12h.01" strokeWidth={3} />
  ),
  semrede: (
    <>
      <path d="M5 9.5a10 10 0 0 1 14 0M8 13a5.5 5.5 0 0 1 8 0" />
      <path d="M12 17.2h.01" strokeWidth={2.6} />
      <path d="M4 4l16 16" />
    </>
  ),
  normas: (
    <path d="M4.5 5h5.5a2 2 0 0 1 2 2v13a2 2 0 0 0-2-2H4.5zM19.5 5H14a2 2 0 0 0-2 2v13a2 2 0 0 1 2-2h5.5z" />
  ),
  frasco: (
    <>
      <path d="M9.5 3h5M10.5 3v6L5 19.2a1.2 1.2 0 0 0 1 1.8h12a1.2 1.2 0 0 0 1-1.8L13.5 9V3" />
      <path d="M7.4 15h9.2" />
    </>
  ),
  ok: (
    <path d="M5 12.5l4.5 4.5L19 7.5" />
  ),
  /* Daqui para baixo, os que o canvas não tinha. */
  /*
   * O cone do CPTu (as campanhas da onda 312): a haste que entra no chão, a
   * luva de atrito e a ponta cônica. Desenho próprio, e não o tripé do
   * Campo, porque os dois aparecem lado a lado no menu do Campo.
   */
  cone: (
    <>
      <path d="M4 7.5h16" />
      <path d="M12 3v8" />
      <rect x="10.25" y="11" width="3.5" height="5" rx="0.5" />
      <path d="M10.25 16h3.5L12 20.5z" />
    </>
  ),
  alerta: (
    <>
      <path d="M12 4 3 20h18z" />
      <path d="M12 10v4.5" />
      <path d="M12 17.3h.01" strokeWidth={2.6} />
    </>
  ),
  /* A engrenagem: o miolo, o aro e os oito dentes — quatro retos e quatro de viés. */
  ajustes: (
    <>
      <circle cx="12" cy="12" r="2.8" />
      <circle cx="12" cy="12" r="6.2" />
      <path d="M12 3v2.8M12 18.2V21M3 12h2.8M18.2 12H21" />
      <path d="M5.6 5.6l2 2M18.4 5.6l-2 2M5.6 18.4l2-2M18.4 18.4l-2-2" />
    </>
  ),
  exportar: (
    <>
      <path d="M12 4v11M8 11l4 4 4-4" />
      <path d="M5 19h14" />
    </>
  ),
  /* Os quatro quadros do «Mais», cada um com a sua linha de ficha. */
  cadastros: (
    <>
      <rect x="4" y="4" width="6.5" height="6.5" />
      <rect x="13.5" y="4" width="6.5" height="6.5" />
      <rect x="4" y="13.5" width="6.5" height="6.5" />
      <rect x="13.5" y="13.5" width="6.5" height="6.5" />
      <path d="M6.5 7.25H8M16 7.25h1.5M6.5 16.75H8M16 16.75h1.5" />
    </>
  ),
  /* O sino dos avisos (onda 548), na mesma grade: o corpo, a borda e o badalo. */
  sino: (
    <>
      <path d="M6.5 16.5V11a5.5 5.5 0 0 1 11 0v5.5l1.5 2h-14z" />
      <path d="M10 20.5a2.1 2.1 0 0 0 4 0" />
    </>
  ),
  /* O clipe do anexo (onda 550, o compositor da caixa de entrada), na mesma grade. */
  anexo: (
    <path d="M19.5 11.5l-7.6 7.6a4.8 4.8 0 0 1-6.8-6.8l7.9-7.9a3.2 3.2 0 0 1 4.5 4.5l-7.9 7.9a1.6 1.6 0 0 1-2.3-2.3l7.3-7.3" />
  ),
  /* O Início do Hub (onda 550), na mesma grade: o telhado, as paredes e a porta. */
  casa: (
    <>
      <path d="M3.5 11 12 4l8.5 7" />
      <path d="M6 9.5V20h12V9.5" />
      <path d="M10 20v-5.5h4V20" />
    </>
  ),
};

/**
 * O ícone. Decorativo (`aria-hidden`): o nome de quem o usa vai em texto ao
 * lado ou no `aria-label` do botão. O tamanho padrão (1,5 rem) está no
 * `celular.css`; quem precisar de outro dá a `className` e o redefine.
 */
export function Icone({ nome, className }: { nome: NomeDoIcone; className?: string }) {
  return (
    <svg
      className={className ? `icone ${className}` : 'icone'}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {DESENHO[nome]}
    </svg>
  );
}

/**
 * Que ícone cada módulo do registro (`modulos.ts`) usa na barra de baixo e
 * na folha «Mais».
 *
 * Uma chave por módulo, uma por linha: o teste 295 lê esta lista e reprova
 * módulo do registro que ficou sem ícone — o mesmo cuidado do teste 18 com
 * as tabelas. `iconeDoModulo` cobre o que o teste não alcança (um módulo que
 * nasce depois): cai em `cadastros`, o ladrilho genérico.
 */
export const ICONE_DO_MODULO: Record<string, NomeDoIcone> = {
  painel: 'painel',
  /* 550 (Tarefa 20): o Início do Hub, a casa — o Painel é do LabManager (M-6 da revisão). */
  inicio: 'casa',
  os: 'os',
  recebimento: 'receber',
  amostras: 'amostras',
  descarte: 'descarte',
  cronograma: 'cronograma',
  fila: 'fila',
  assinatura: 'verificar',
  estufa: 'estufa',
  /* A bancada: programar e todos os métodos de ensaio são o mesmo frasco. */
  ensaios: 'frasco',
  limites: 'frasco',
  'ensaio-situacao': 'frasco',
  granulometria: 'frasco',
  massa_especifica: 'frasco',
  classificacao: 'frasco',
  isc: 'frasco',
  adensamento: 'frasco',
  triaxial: 'frasco',
  'cisalhamento-direto': 'frasco',
  mr: 'frasco',
  umidade: 'frasco',
  limite_liquidez: 'frasco',
  limite_plasticidade: 'frasco',
  compactacao: 'frasco',
  mapa: 'mapa',
  relatorios: 'relatorio',
  medicao: 'medicao',
  itens_medicao: 'medicao',
  portal: 'relatorio',
  equipamentos: 'equipamento',
  padroes: 'equipamento',
  normas: 'normas',
  calculos: 'normas',
  nao_conformidades: 'alerta',
  documentos: 'relatorio',
  competencias: 'verificar',
  treinamentos: 'normas',
  campo: 'campo',
  campo_da_os: 'campo',
  cptu: 'cone',
  /* 0278 (ondas 355–359): as sondagens SPT, o desenho do furo de campo. */
  spt: 'campo',
  cadastros: 'cadastros',
  clientes: 'cadastros',
  fornecedores: 'cadastros',
  locais: 'cadastros',
  /* 0500 (onda 347): a caixa de entrada do Comercial, a bandeja de entrada. */
  caixa: 'receber',
  /* 0501 (onda 347): as oportunidades do Comercial, o funil. */
  oportunidades: 'filtro',
  /* 0509 (onda 550): o funil que anda sozinho (o quadro chega depois). */
  funil: 'filtro',
  /* 0510 (onda 550): o Radar das conversas e propostas paradas. */
  radar: 'alerta',
  /* 0531 (onda 552): o painel do Comercial, o desenho do Painel. */
  painel_comercial: 'painel',
  /* 0508 (onda 550): os contatos e as tarefas do Comercial. */
  contatos: 'pessoa',
  tarefas: 'ok',
  /* 0506 (onda 550): os ajustes do Comercial, o mesmo desenho dos Ajustes (a tela é da Tarefa 16). */
  ajustes_comercial: 'ajustes',
  /* 0511 (onda 552): o agente de IA, o que confere antes de agir. */
  agente: 'verificar',
  usuarios: 'pessoa',
  ajustes: 'ajustes',
  exportacao: 'exportar',
  padrao: 'tema',
  categorias_equipamento: 'equipamento',
  relatos: 'relatar',
  exclusao_definitiva: 'descarte',
  /* 0503 (onda 548): os avisos, o sino. */
  avisos: 'sino',
};

/**
 * O ícone de um módulo pela chave do registro; desconhecido cai em «cadastros».
 * Só as chaves do próprio mapa contam (`constructor` não é módulo); e
 * `hasOwnProperty.call`, não `Object.hasOwn`, que falta nos aparelhos que o
 * Vite ainda atende (Safari 14, Chrome 87).
 */
export function iconeDoModulo(chave: string): NomeDoIcone {
  return Object.prototype.hasOwnProperty.call(ICONE_DO_MODULO, chave) ? ICONE_DO_MODULO[chave] : 'cadastros';
}
